import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import {
  AButton,
  ACheckbox,
  ADataTable,
  APagination
} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {appConfig} from "config/defaultConfig";
import {t} from "i18next";
import {useDispatch, useSelector} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import TableEmptyState from "components/Table/TableEmptyState";
import {SavedInvestigationsContext} from "contexts/SavedInvestigationsContext";
import {getSnapshot} from "store/features/investigationSlice";
import {
  selectInvestigations,
  selectInvestigationsLoading,
  selectInvestigationTotalRows,
  selectSavedInvestigationsSearchText,
  selectSavedInvestigationsZeroResults
} from "store/selectors";
import {selectSnapshotLoading} from "store/selectors/investigationSelectors";
import {REDUCED_RESULTS_PER_PAGE} from "models/options";
import {tableLoadingState} from "utils/dataTable";
import {getShortId} from "utils/misc";
import TooltipDelay from "../../../components/TooltipDelay";
import BulkAction from "./BulkAction";
import SavedInvestigationsContextMenu from "./SavedInvestigationsContextMenu";
import css from "./SavedInvestigationsTable.module.scss";

const {ROUTES} = appConfig.APP;

const TooltipField = ({id, title, subtitle, showButton}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector(selectSnapshotLoading);

  const expandedFieldRef = useRef(null);

  const handleClick = () =>
    dispatch(
      getSnapshot({id: getShortId(id), short_description: subtitle, navigate})
    );

  return (
    <TooltipDelay subtitle={subtitle} title={title}>
      <div ref={expandedFieldRef} className="text-truncate">
        {showButton ? (
          <AButton
            className={"px-0 d-inline"}
            component={Link}
            tertiary
            data-auto-enriched={subtitle.indexOf("AUTO-ENRICHED:") > -1}
            onClick={handleClick}
            disabled={isLoading}>
            {subtitle}
          </AButton>
        ) : (
          subtitle
        )}
      </div>
    </TooltipDelay>
  );
};

const SavedInvestigationsTable = ({
  resultsPerPage = REDUCED_RESULTS_PER_PAGE
}) => {
  const investigations = useSelector(selectInvestigations);
  const isLoading = useSelector(selectInvestigationsLoading);
  const hasZeroInvestigations = useSelector(
    selectSavedInvestigationsZeroResults
  );
  const searchText = useSelector(selectSavedInvestigationsSearchText);
  const totalInvestigationsCount = useSelector(selectInvestigationTotalRows);
  const savedInvestigationsContextData = useContext(SavedInvestigationsContext);

  const {
    page,
    filterSettings,
    dispatchFilterSettings,
    setPage,
    pageCount,
    setPageCount
  } = savedInvestigationsContextData;

  const isEmpty = !isLoading && hasZeroInvestigations;

  const hasFiltersApplied = searchText.length > 0;

  const [tableModel, setTableModel] = useState([...investigations]);

  useEffect(() => {
    setTableModel([...investigations]);
  }, [investigations]);

  const handleBulkActionChecked = (checked) => {
    setTableModel(
      tableModel.map((investigation) => ({...investigation, checked: checked}))
    );
  };

  const handleSingleRowChecked = (rowData) => {
    setTableModel(
      tableModel.map((investigation) =>
        investigation.id !== rowData.id
          ? investigation
          : {...investigation, checked: !rowData.checked}
      )
    );
  };

  const headers = [
    {
      name: (
        <BulkAction
          checked={
            !!tableModel.length &&
            tableModel.every((investigation) => investigation.checked)
          }
          indeterminate={
            !!tableModel.length &&
            tableModel.some((investigation) => investigation.checked)
          }
          disabled={isLoading || !investigations.length}
          investigations={tableModel.filter(
            (investigation) => investigation.checked
          )}
          handleOnSelect={handleBulkActionChecked}
        />
      ),
      className: css.cellCheckboxAction,
      cell: {
        component: (rowData) => (
          <ACheckbox
            data-testid={"BulkAction-Checkbox"}
            className="bulk-action d-flex align-center"
            checked={rowData.checked}
            disabled={isLoading}
            onClick={() => !isLoading && handleSingleRowChecked(rowData)}
          />
        )
      }
    },
    {
      name: t("investigate.savedInvestigations.table.name"),
      key: "short_description",
      className: css.cellName,
      cell: {
        className: css.truncateCellText,
        component: ({id, short_description: shortDescription}) => {
          return (
            <TooltipField
              id={id}
              subtitle={shortDescription}
              title={"Name"}
              showButton
            />
          );
        }
      }
    },
    {
      name: t("investigate.savedInvestigations.table.description"),
      key: "description",
      className: css.cellDescription,
      cell: {
        className: css.cellDescription,
        component: ({description}) => {
          return (
            <TooltipField
              subtitle={description}
              title={t("investigate.savedInvestigations.table.description")}
            />
          );
        }
      }
    },
    {
      name: (
        <TooltipDelay
          title={t("investigate.savedInvestigations.table.timestamp")}>
          <div className={"text-truncate"}>
            <p className={css.truncatedContents}>
              {t("investigate.savedInvestigations.table.timestamp")}
            </p>
          </div>
        </TooltipDelay>
      ),
      key: "timestamp",
      sortable: true,
      sort: true,
      className: css.cellTimestamp,
      cell: {
        className: css.truncateCellText,
        component: ({timestamp}) => {
          return (
            <TooltipField
              subtitle={timestamp}
              title={t("investigate.savedInvestigations.table.timestamp")}
            />
          );
        }
      }
    },
    {
      name: t("investigate.savedInvestigations.table.owner"),
      key: "owner",
      className: css.cellCreatedBy,
      cell: {
        className: css.truncateCellText,
        component: ({owner}) => {
          return (
            <TooltipField
              subtitle={owner}
              title={t("investigate.savedInvestigations.table.owner")}
            />
          );
        }
      }
    },
    {
      key: "options",
      cell: {
        component: (investigation) => (
          <SavedInvestigationsContextMenu investigation={investigation} />
        )
      },
      className: css.cellOptions
    }
  ];

  const items = isLoading ? tableLoadingState(pageCount) : tableModel;

  useEffect(() => {
    setPageCount(resultsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSortChange = useCallback(
    /**
     * @param {{key: string, direction: "asc"|"desc"}} sort
     */
    ({direction, key}) => {
      dispatchFilterSettings({order: direction, orderBy: key});
    },
    [dispatchFilterSettings]
  );

  return (
    <div
      className={css.savedInvestigationsTable}
      data-testid="saved-investigations-table">
      {isEmpty && (
        <TableEmptyState
          entityNamePlural={t("investigate.savedInvestigations.title")}
          hasFiltersApplied={hasFiltersApplied}
        />
      )}

      {!isEmpty && (
        <>
          <ADataTable
            data-testid={"SavedInvestigationsTable-DataTable"}
            tight
            headers={headers}
            items={items}
            className={classNames("mx-auto", css.savedInvestigationsTable)}
            sort={{
              key: filterSettings.orderBy,
              direction: filterSettings.order
            }}
            onSort={handleSortChange}
            disableSortReset
            disabled={isLoading || hasZeroInvestigations}
            isRowSelected={(rowData) => rowData?.checked}
            truncateHeaders
          />
          <div className="d-flex align-baseline justify-space-between">
            {pageCount <= REDUCED_RESULTS_PER_PAGE && (
              <div className="d-flex justify-start align-center">
                <Link
                  to={ROUTES.SAVED_INVESTIGATIONS}
                  data-testid="saved-investigations-table-view-all-button">
                  {t("investigate.savedInvestigations.showAll")}
                </Link>
              </div>
            )}

            <div
              className="d-flex justify-end"
              style={{
                width: pageCount > REDUCED_RESULTS_PER_PAGE ? "100%" : "unset"
              }}>
              <APagination
                className="mt-2"
                page={page}
                onPageChange={(value) => setPage(value)}
                resultsPerPage={pageCount}
                total={totalInvestigationsCount}
                onResultsPerPageChange={(value) => setPageCount(value)}
                disabled={isLoading || hasZeroInvestigations}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SavedInvestigationsTable;
